import { useState, useEffect } from 'react';
import { api } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { 
  Search, Calendar, Filter, Download, RefreshCw,
  Receipt, Clock, CreditCard, User, UtensilsCrossed,
  ChefHat, Check, Bell, FileText, X, Eye,
  TrendingUp, DollarSign, ShoppingBag, Printer, MessageCircle, Phone
} from 'lucide-react';
import { toast } from 'sonner';

// Order status config
const ORDER_STATUS = {
  pending: { label: 'Pending', color: 'bg-red-500 text-white', icon: Bell },
  preparing: { label: 'Preparing', color: 'bg-amber-500 text-white', icon: ChefHat },
  ready: { label: 'Ready', color: 'bg-green-500 text-white', icon: Check },
  served: { label: 'Served', color: 'bg-blue-500 text-white', icon: UtensilsCrossed },
  completed: { label: 'Completed', color: 'bg-emerald-600 text-white', icon: Check },
  cancelled: { label: 'Cancelled', color: 'bg-gray-500 text-white', icon: X },
  refunded: { label: 'Refunded', color: 'bg-purple-500 text-white', icon: RefreshCw }
};

// Order type labels
const ORDER_TYPES = {
  dine_in: { label: 'Dine In', icon: UtensilsCrossed },
  takeaway: { label: 'Takeaway', icon: ShoppingBag },
  room_service: { label: 'Room Service', icon: Receipt }
};

export default function OrderHistory() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [restaurants, setRestaurants] = useState([]);
  
  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [orderTypeFilter, setOrderTypeFilter] = useState('all');
  const [restaurantFilter, setRestaurantFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  
  // Order detail dialog
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [orderDetailOpen, setOrderDetailOpen] = useState(false);
  
  // Stats
  const [stats, setStats] = useState({
    totalOrders: 0,
    totalRevenue: 0,
    avgOrderValue: 0,
    completedOrders: 0
  });
  
  // Invoice actions dialog
  const [invoiceActionsOpen, setInvoiceActionsOpen] = useState(false);
  const [currentInvoice, setCurrentInvoice] = useState(null);
  const [whatsappNumber, setWhatsappNumber] = useState('');
  const [sendingWhatsApp, setSendingWhatsApp] = useState(false);

  useEffect(() => {
    loadRestaurants();
    loadOrders();
  }, []);

  const loadRestaurants = async () => {
    try {
      const res = await api.get('/restaurants');
      setRestaurants(res.data);
    } catch (error) {
      console.error('Failed to load restaurants');
    }
  };

  const loadOrders = async () => {
    setLoading(true);
    try {
      // Build query params
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (orderTypeFilter !== 'all') params.append('order_type', orderTypeFilter);
      if (restaurantFilter !== 'all') params.append('restaurant_id', restaurantFilter);
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);
      
      const res = await api.get(`/orders?${params.toString()}`);
      let filteredOrders = res.data;
      
      // Client-side search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        filteredOrders = filteredOrders.filter(order => 
          order.id?.toLowerCase().includes(query) ||
          order.customer_name?.toLowerCase().includes(query) ||
          order.table_number?.toString().includes(query) ||
          order.room_number?.toString().includes(query)
        );
      }
      
      // Sort by date descending
      filteredOrders.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      
      setOrders(filteredOrders);
      
      // Calculate stats
      const totalRevenue = filteredOrders.reduce((sum, o) => sum + (o.total_amount || 0), 0);
      const completedOrders = filteredOrders.filter(o => ['served', 'completed'].includes(o.status)).length;
      
      setStats({
        totalOrders: filteredOrders.length,
        totalRevenue,
        avgOrderValue: filteredOrders.length > 0 ? totalRevenue / filteredOrders.length : 0,
        completedOrders
      });
    } catch (error) {
      toast.error('Failed to load orders');
    } finally {
      setLoading(false);
    }
  };

  // Apply filters
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      loadOrders();
    }, 300);
    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, orderTypeFilter, restaurantFilter, dateFrom, dateTo, searchQuery]);

  const exportToCSV = () => {
    const headers = ['Order ID', 'Date', 'Time', 'Type', 'Table/Room', 'Customer', 'Items', 'Status', 'Payment', 'Total'];
    
    const rows = orders.map(order => [
      order.id?.slice(-8).toUpperCase() || '',
      new Date(order.created_at).toLocaleDateString(),
      new Date(order.created_at).toLocaleTimeString(),
      ORDER_TYPES[order.order_type]?.label || order.order_type,
      order.table_number || order.room_number || '-',
      order.customer_name || '-',
      order.items?.map(i => `${i.quantity}x ${i.item_name}`).join('; ') || '',
      ORDER_STATUS[order.status]?.label || order.status,
      order.payment_method || '-',
      order.total_amount?.toFixed(2) || '0.00'
    ]);
    
    const csvContent = [headers, ...rows].map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `order-history-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    
    toast.success('Orders exported to CSV');
  };

  // Generate bill/invoice for completed order
  const generateBill = async (order) => {
    try {
      const invoiceData = {
        order_ids: [order.id],
        customer_name: order.customer_name || 'Guest',
        notes: order.notes || ''
      };

      const res = await api.post('/invoices/from-orders', invoiceData);
      
      // Update order status to completed
      await api.patch(`/orders/${order.id}/status?status=completed`);
      
      toast.success('Invoice generated - Order marked as Completed');
      
      // Refresh orders and wait for it
      await loadOrders();
      
      // Show invoice actions dialog
      setCurrentInvoice(res.data);
      setWhatsappNumber(order.customer_phone || '');
      setInvoiceActionsOpen(true);
    } catch (error) {
      // Handle validation errors properly
      const errorDetail = error.response?.data?.detail;
      let errorMessage = 'Failed to generate invoice';
      
      if (typeof errorDetail === 'string') {
        errorMessage = errorDetail;
      } else if (Array.isArray(errorDetail)) {
        errorMessage = errorDetail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
      }
      
      toast.error(errorMessage);
    }
  };

  // Print invoice
  const printInvoice = async (invoiceId) => {
    try {
      const res = await api.get(`/invoices/${invoiceId}/pdf`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      
      // Open PDF in new tab for printing
      const printWindow = window.open(url, '_blank');
      if (printWindow) {
        printWindow.onload = () => {
          printWindow.print();
        };
      }
      toast.success('Opening print dialog...');
    } catch (error) {
      toast.error('Failed to print invoice');
    }
  };

  // Send invoice via WhatsApp
  const sendWhatsApp = async () => {
    if (!currentInvoice?.id) return;
    if (!whatsappNumber) {
      toast.error('Please enter WhatsApp number');
      return;
    }
    
    setSendingWhatsApp(true);
    try {
      await api.post(`/invoices/${currentInvoice.id}/send-whatsapp`, {
        phone_number: whatsappNumber
      });
      toast.success('Invoice sent via WhatsApp!');
      setInvoiceActionsOpen(false);
    } catch (error) {
      const errorDetail = error.response?.data?.detail;
      toast.error(typeof errorDetail === 'string' ? errorDetail : 'Failed to send WhatsApp');
    } finally {
      setSendingWhatsApp(false);
    }
  };

  // Mark order as completed (without generating bill)
  const markAsCompleted = async (orderId) => {
    try {
      await api.patch(`/orders/${orderId}/status?status=completed`);
      toast.success('Order marked as Completed');
      await loadOrders();
    } catch (error) {
      toast.error('Failed to update order status');
    }
  };

  // View existing invoice
  const viewInvoice = (invoiceId) => {
    window.open(`/invoices?id=${invoiceId}`, '_blank');
  };
  
  // Open invoice actions for existing invoice
  const openInvoiceActions = async (invoiceId) => {
    try {
      const res = await api.get(`/invoices/${invoiceId}`);
      setCurrentInvoice(res.data);
      setWhatsappNumber('');
      setInvoiceActionsOpen(true);
    } catch (error) {
      toast.error('Failed to load invoice');
    }
  };

  const getTimeTaken = (order) => {
    if (!order.created_at || !order.updated_at) return '-';
    const start = new Date(order.created_at);
    const end = new Date(order.updated_at);
    const diffMins = Math.round((end - start) / 60000);
    if (diffMins < 60) return `${diffMins}m`;
    const hours = Math.floor(diffMins / 60);
    const mins = diffMins % 60;
    return `${hours}h ${mins}m`;
  };

  const viewOrderDetail = (order) => {
    setSelectedOrder(order);
    setOrderDetailOpen(true);
  };

  return (
    <div className="space-y-6" data-testid="order-history-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Receipt className="h-6 w-6 text-primary" />
            Order History
          </h1>
          <p className="text-muted-foreground">View and manage past orders</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={loadOrders} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button onClick={exportToCSV} disabled={orders.length === 0}>
            <Download className="h-4 w-4 mr-2" />
            Export CSV
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                <Receipt className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Orders</p>
                <p className="text-2xl font-bold">{stats.totalOrders}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                <DollarSign className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Revenue</p>
                <p className="text-2xl font-bold">₹{stats.totalRevenue.toFixed(0)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
                <TrendingUp className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Avg Order Value</p>
                <p className="text-2xl font-bold">₹{stats.avgOrderValue.toFixed(0)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
                <Check className="h-5 w-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Completed</p>
                <p className="text-2xl font-bold">{stats.completedOrders}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4 items-end">
            {/* Search */}
            <div className="flex-1 min-w-[200px]">
              <Label className="text-xs text-muted-foreground">Search</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Order ID, customer, table..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                  data-testid="search-orders"
                />
              </div>
            </div>
            
            {/* Status Filter */}
            <div className="w-[150px]">
              <Label className="text-xs text-muted-foreground">Status</Label>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger data-testid="status-filter">
                  <SelectValue placeholder="All Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="preparing">Preparing</SelectItem>
                  <SelectItem value="ready">Ready</SelectItem>
                  <SelectItem value="served">Served</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Order Type Filter */}
            <div className="w-[150px]">
              <Label className="text-xs text-muted-foreground">Order Type</Label>
              <Select value={orderTypeFilter} onValueChange={setOrderTypeFilter}>
                <SelectTrigger data-testid="type-filter">
                  <SelectValue placeholder="All Types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="dine_in">Dine In</SelectItem>
                  <SelectItem value="takeaway">Takeaway</SelectItem>
                  <SelectItem value="room_service">Room Service</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Restaurant Filter */}
            <div className="w-[180px]">
              <Label className="text-xs text-muted-foreground">Restaurant</Label>
              <Select value={restaurantFilter} onValueChange={setRestaurantFilter}>
                <SelectTrigger data-testid="restaurant-filter">
                  <SelectValue placeholder="All Restaurants" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Restaurants</SelectItem>
                  {restaurants.map(r => (
                    <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Date From */}
            <div className="w-[150px]">
              <Label className="text-xs text-muted-foreground">From Date</Label>
              <Input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                data-testid="date-from"
              />
            </div>
            
            {/* Date To */}
            <div className="w-[150px]">
              <Label className="text-xs text-muted-foreground">To Date</Label>
              <Input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                data-testid="date-to"
              />
            </div>
            
            {/* Clear Filters */}
            {(searchQuery || statusFilter !== 'all' || orderTypeFilter !== 'all' || restaurantFilter !== 'all' || dateFrom || dateTo) && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setSearchQuery('');
                  setStatusFilter('all');
                  setOrderTypeFilter('all');
                  setRestaurantFilter('all');
                  setDateFrom('');
                  setDateTo('');
                }}
              >
                <X className="h-4 w-4 mr-1" />
                Clear
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Orders Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="h-8 w-8 rounded-full border-4 border-primary border-t-transparent animate-spin" />
            </div>
          ) : orders.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Receipt className="h-12 w-12 mx-auto mb-2 opacity-50" />
              <p>No orders found</p>
              <p className="text-sm">Try adjusting your filters</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="text-left p-3 font-medium">Order ID</th>
                    <th className="text-left p-3 font-medium">Date & Time</th>
                    <th className="text-left p-3 font-medium">Type</th>
                    <th className="text-left p-3 font-medium">Table/Room</th>
                    <th className="text-left p-3 font-medium">Customer</th>
                    <th className="text-left p-3 font-medium">Items</th>
                    <th className="text-left p-3 font-medium">Status</th>
                    <th className="text-left p-3 font-medium">Payment</th>
                    <th className="text-left p-3 font-medium">Time Taken</th>
                    <th className="text-right p-3 font-medium">Total</th>
                    <th className="text-center p-3 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map((order) => {
                    const statusInfo = ORDER_STATUS[order.status] || ORDER_STATUS.pending;
                    const StatusIcon = statusInfo.icon;
                    const typeInfo = ORDER_TYPES[order.order_type] || ORDER_TYPES.dine_in;
                    const TypeIcon = typeInfo.icon;
                    
                    return (
                      <tr key={order.id} className="border-b hover:bg-muted/30 transition-colors">
                        <td className="p-3">
                          <span className="font-mono text-sm">
                            #{order.id?.slice(-8).toUpperCase()}
                          </span>
                        </td>
                        <td className="p-3">
                          <div className="text-sm">
                            {new Date(order.created_at).toLocaleDateString()}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {new Date(order.created_at).toLocaleTimeString()}
                          </div>
                        </td>
                        <td className="p-3">
                          <Badge variant="outline" className="font-normal">
                            <TypeIcon className="h-3 w-3 mr-1" />
                            {typeInfo.label}
                          </Badge>
                        </td>
                        <td className="p-3">
                          {order.table_number ? (
                            <span>Table {order.table_number}</span>
                          ) : order.room_number ? (
                            <span>Room {order.room_number}</span>
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </td>
                        <td className="p-3">
                          <div className="flex items-center gap-1">
                            <User className="h-3 w-3 text-muted-foreground" />
                            <span className="text-sm">{order.customer_name || '-'}</span>
                          </div>
                        </td>
                        <td className="p-3">
                          <span className="text-sm">
                            {order.items?.length || 0} items
                          </span>
                        </td>
                        <td className="p-3">
                          <Badge className={statusInfo.color}>
                            <StatusIcon className="h-3 w-3 mr-1" />
                            {statusInfo.label}
                          </Badge>
                        </td>
                        <td className="p-3">
                          {order.payment_method ? (
                            <Badge variant="outline" className="font-normal">
                              <CreditCard className="h-3 w-3 mr-1" />
                              {order.payment_method}
                            </Badge>
                          ) : (
                            <span className="text-muted-foreground text-sm">-</span>
                          )}
                        </td>
                        <td className="p-3">
                          <div className="flex items-center gap-1 text-sm text-muted-foreground">
                            <Clock className="h-3 w-3" />
                            {getTimeTaken(order)}
                          </div>
                        </td>
                        <td className="p-3 text-right font-bold">
                          ₹{(order.total_amount || 0).toFixed(0)}
                        </td>
                        <td className="p-3 text-center">
                          <div className="flex items-center justify-center gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => viewOrderDetail(order)}
                              data-testid={`view-order-${order.id}`}
                              title="View Details"
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            {['served', 'completed'].includes(order.status) && !order.invoice_id && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => generateBill(order)}
                                data-testid={`generate-bill-${order.id}`}
                                title="Generate Bill & Complete"
                                className="text-green-600 hover:text-green-700 hover:bg-green-50"
                              >
                                <FileText className="h-4 w-4" />
                              </Button>
                            )}
                            {order.status === 'served' && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => markAsCompleted(order.id)}
                                data-testid={`mark-complete-${order.id}`}
                                title="Mark as Completed"
                                className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                              >
                                <Check className="h-4 w-4" />
                              </Button>
                            )}
                            {order.invoice_id && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openInvoiceActions(order.invoice_id)}
                                data-testid={`view-invoice-${order.id}`}
                                title="Invoice Actions (Print/WhatsApp)"
                                className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                              >
                                <Receipt className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Order Detail Dialog */}
      <Dialog open={orderDetailOpen} onOpenChange={setOrderDetailOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Order Details
              {selectedOrder && (
                <span className="font-mono text-sm text-muted-foreground">
                  #{selectedOrder.id?.slice(-8).toUpperCase()}
                </span>
              )}
            </DialogTitle>
          </DialogHeader>
          
          {selectedOrder && (
            <div className="space-y-4">
              {/* Order Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-muted-foreground">Date & Time</Label>
                  <p>{new Date(selectedOrder.created_at).toLocaleString()}</p>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Status</Label>
                  <div>
                    <Badge className={ORDER_STATUS[selectedOrder.status]?.color}>
                      {ORDER_STATUS[selectedOrder.status]?.label}
                    </Badge>
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Order Type</Label>
                  <p>{ORDER_TYPES[selectedOrder.order_type]?.label || selectedOrder.order_type}</p>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Table/Room</Label>
                  <p>{selectedOrder.table_number ? `Table ${selectedOrder.table_number}` : selectedOrder.room_number ? `Room ${selectedOrder.room_number}` : '-'}</p>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Customer</Label>
                  <p>{selectedOrder.customer_name || '-'}</p>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Payment Method</Label>
                  <p>{selectedOrder.payment_method || 'Not paid yet'}</p>
                </div>
                {selectedOrder.server_name && (
                  <div>
                    <Label className="text-xs text-muted-foreground">Server</Label>
                    <p>{selectedOrder.server_name}</p>
                  </div>
                )}
                <div>
                  <Label className="text-xs text-muted-foreground">Time Taken</Label>
                  <p>{getTimeTaken(selectedOrder)}</p>
                </div>
              </div>
              
              {/* Items */}
              <div>
                <Label className="text-xs text-muted-foreground mb-2 block">Items</Label>
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-muted/50">
                        <th className="text-left p-2 text-sm">Item</th>
                        <th className="text-center p-2 text-sm">Qty</th>
                        <th className="text-right p-2 text-sm">Price</th>
                        <th className="text-right p-2 text-sm">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedOrder.items?.map((item, idx) => (
                        <tr key={idx} className="border-t">
                          <td className="p-2">{item.item_name}</td>
                          <td className="p-2 text-center">{item.quantity}</td>
                          <td className="p-2 text-right">₹{item.price}</td>
                          <td className="p-2 text-right">₹{(item.price * item.quantity).toFixed(0)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              
              {/* Notes */}
              {selectedOrder.notes && (
                <div>
                  <Label className="text-xs text-muted-foreground">Notes</Label>
                  <p className="bg-yellow-50 dark:bg-yellow-900/20 p-2 rounded text-sm">
                    {selectedOrder.notes}
                  </p>
                </div>
              )}
              
              {/* Totals */}
              <div className="border-t pt-4 space-y-1">
                <div className="flex justify-between text-sm">
                  <span>Subtotal</span>
                  <span>₹{(selectedOrder.subtotal || 0).toFixed(2)}</span>
                </div>
                {selectedOrder.discount > 0 && (
                  <div className="flex justify-between text-sm text-green-600">
                    <span>Discount</span>
                    <span>-₹{selectedOrder.discount.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between text-sm">
                  <span>Tax</span>
                  <span>₹{(selectedOrder.tax || 0).toFixed(2)}</span>
                </div>
                <div className="flex justify-between font-bold text-lg border-t pt-2">
                  <span>Total</span>
                  <span>₹{(selectedOrder.total_amount || 0).toFixed(2)}</span>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Invoice Actions Dialog */}
      <Dialog open={invoiceActionsOpen} onOpenChange={setInvoiceActionsOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Receipt className="h-5 w-5 text-green-600" />
              Invoice Generated
            </DialogTitle>
          </DialogHeader>
          
          {currentInvoice && (
            <div className="space-y-4">
              {/* Invoice Summary */}
              <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-muted-foreground">Invoice #</span>
                  <span className="font-mono font-bold">{currentInvoice.invoice_number}</span>
                </div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-muted-foreground">Customer</span>
                  <span>{currentInvoice.customer_name || 'Guest'}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Total Amount</span>
                  <span className="text-xl font-bold text-green-600">₹{(currentInvoice.total_amount || 0).toFixed(2)}</span>
                </div>
              </div>
              
              {/* Action Buttons */}
              <div className="grid grid-cols-2 gap-3">
                <Button 
                  variant="outline" 
                  className="h-16 flex-col gap-1"
                  onClick={() => printInvoice(currentInvoice.id)}
                >
                  <Printer className="h-5 w-5" />
                  <span className="text-xs">Print Invoice</span>
                </Button>
                <Button 
                  variant="outline" 
                  className="h-16 flex-col gap-1"
                  onClick={() => viewInvoice(currentInvoice.id)}
                >
                  <Eye className="h-5 w-5" />
                  <span className="text-xs">View Invoice</span>
                </Button>
              </div>
              
              {/* WhatsApp Section */}
              <div className="border-t pt-4">
                <Label className="text-sm font-medium mb-2 block">Send via WhatsApp</Label>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Enter WhatsApp number (with country code)"
                      value={whatsappNumber}
                      onChange={(e) => setWhatsappNumber(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                  <Button 
                    onClick={sendWhatsApp}
                    disabled={sendingWhatsApp || !whatsappNumber}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    {sendingWhatsApp ? (
                      <RefreshCw className="h-4 w-4 animate-spin" />
                    ) : (
                      <MessageCircle className="h-4 w-4" />
                    )}
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Enter number with country code (e.g., +91 9876543210)
                </p>
              </div>
              
              {/* Close Button */}
              <Button 
                variant="outline" 
                className="w-full"
                onClick={() => setInvoiceActionsOpen(false)}
              >
                Done
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
